import { ChangeDetectionStrategy, Component } from "@angular/core";
import { Store } from "@ngrx/store";
import { RootState } from "src/app/store";
import { AppRoutes, RouteParams } from "src/app/constants";
import { ActivatedRoute } from "@angular/router";
import * as EventActions from "src/app/store/event/actions";
import { Observable, Subscription } from "rxjs";
import { EventContentDto, EventDto, UserDto } from "@api";
import { filter } from "rxjs/operators";
import * as FromEvent from "src/app/store/event/selectors";
import * as FromUser from "src/app/store/user/selectors";
import { AlertController, PopoverController } from "@ionic/angular";
import { PopoverButton, PopoverComponent } from "src/app/components/popover/popover.component";
import { NavController } from "@ionic/angular";

@Component({
  selector: "app-event-content-detail",
  templateUrl: "./event-content-detail.page.html",
  styleUrls: ["./event-content-detail.page.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventContentDetailPage {
  public routes: typeof AppRoutes = AppRoutes;
  public loading$: Observable<boolean> = this.store.select(FromEvent.selectLoading);
  public content$: Observable<EventContentDto> = this.store.select(FromEvent.selectContent);
  public events$: Observable<EventDto[]>;
  public user$: Observable<UserDto> = this.store.select(FromUser.selectUser);
  public breadcrumbs = [
    { name: "Dashboard", link: `/${this.routes.home}` },
    { name: "Veranstaltungen verwalten", link: `/${this.routes.eventContents}` },
    { name: "Details zur Veranstaltung" },
  ];

  private contentId = this.activatedRoute.snapshot.paramMap.get(RouteParams.contentId);
  private $content: Subscription;

  constructor(
    private store: Store<RootState>,
    private activatedRoute: ActivatedRoute,
    private navController: NavController,
    private popoverController: PopoverController,
    private alertController: AlertController,
  ) {}

  public trackBy(_: number, event: EventDto): string {
    return event.id;
  }

  public async presentGeneralPopover(event: Event): Promise<void> {
    const buttons: PopoverButton[] = [
      {
        text: "Inhalt bearbeiten",
        handler: (): Promise<boolean> => this.navController.navigateForward([AppRoutes.eventContents, this.contentId, AppRoutes.edit]),
      },
      {
        text: "Veranstaltung erstellen",
        handler: (): Promise<boolean> => this.navController.navigateForward([AppRoutes.eventContents, this.contentId, AppRoutes.add]),
      },
      { text: "löschen", handler: (): Promise<void> => this.deleteEventContent(), danger: true },
    ];
    const popover = await this.popoverController.create({
      component: PopoverComponent,
      componentProps: { buttons },
      event,
    });

    await popover.present();
  }

  public async presentEventPopover(event: Event, eventDto: EventDto): Promise<void> {
    const buttons: PopoverButton[] = [
      {
        text: "ereignis bearbeiten",
        handler: (): Promise<boolean> => this.navController.navigateForward([AppRoutes.eventContents, this.contentId, eventDto.id]),
      },
      {
        text: "ereignis löschen",
        handler: (): Promise<void> => this.deleteEvent(eventDto),
        danger: true,
      },
    ];
    const popover = await this.popoverController.create({
      component: PopoverComponent,
      componentProps: { buttons },
      event,
    });

    await popover.present();
  }

  protected ionViewWillEnter(): void {
    this.$content = this.content$.pipe(filter(content => !!content)).subscribe(content => {
      this.events$ = this.store.select(FromEvent.selectContentEvents(content.id));
      this.breadcrumbs = [
        { name: "Dashboard", link: `/${this.routes.home}` },
        { name: "Veranstaltungen verwalten", link: `/${this.routes.eventContents}` },
        { name: content.title },
      ];
    });
  }

  protected ionViewDidEnter(): void {
    if (this.contentId) {
      this.store.dispatch(EventActions.getReferents());
      this.store.dispatch(EventActions.getEventContent({ contentId: this.contentId }));
      this.store.dispatch(EventActions.getEventContentEvents({ contentId: this.contentId }));
    }
  }

  protected ionWillLeave(): void {
    this.$content?.unsubscribe();
  }

  private async deleteEventContent(): Promise<void> {
    const alert = await this.alertController.create({
      header: "Inhalt löschen",
      message: "Wenn Sie diesen Inhalt löschen, werden alle damit verbundenen Ereignisse entfernt. Möchten Sie fortfahren?",
      buttons: [
        { text: "Abbrechen", role: "cancel" },
        { text: "Ja, löschen", handler: (): void => this.store.dispatch(EventActions.removeEventContent({ contentId: this.contentId })) },
      ],
      backdropDismiss: false,
    });

    await alert.present();
  }

  private async deleteEvent(event: EventDto): Promise<void> {
    const alert = await this.alertController.create({
      header: "Ereignis löschen",
      message: "Bestätigen Sie, dass Sie dieses Ereignis löschen möchten?",
      buttons: [
        { text: "Abbrechen", role: "cancel" },
        { text: "Ja, löschen", handler: (): void => this.store.dispatch(EventActions.removeEvent({ eventId: event.id })) },
      ],
      backdropDismiss: false,
    });

    await alert.present();
  }
}
